import { ShimmerLoadingIframe } from "@albertine/design-system/components/ShimmerLoadingIframe";
import { parseRequestExamplesMessageData } from "@albertine/types";
import { type RefObject, useEffect, useEffectEvent, useRef } from "react";
import { useKeycloakToken } from "../../common/hooks/useKeycloakToken";
import config from "../../config";
import { sharedMiniAppUrl } from "../../utils/sharedMiniAppUrl";
import { Flex } from "../Common/Flex";
import Label from "../Common/Label";

const TOKEN_REQUEST_TYPE = "requestExamples.auth.token.request";
const TOKEN_RESPONSE_TYPE = "requestExamples.auth.token.response";
const TOKEN_ERROR_TYPE = "requestExamples.auth.token.error";

type SharedMiniAppEmbedProps = {
    path: "booking" | "proposal";
    resourceId: string;
    tenant: string;
    locale: string;
    hash?: string;
};

export function SharedMiniAppEmbed({
    path,
    resourceId,
    tenant,
    locale,
    hash,
}: SharedMiniAppEmbedProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const getKeycloakToken = useKeycloakToken();
    const iframeUrl = sharedMiniAppUrl({
        hostingOrigin: config.miniAppsHosting,
        path,
        tenant,
        locale,
        id: resourceId,
        parentOrigin: window.location.origin,
        hash,
    });
    const iframeOrigin = iframeUrl ? new URL(iframeUrl).origin : null;

    useMiniAppTokenRelay({
        iframeRef,
        iframeOrigin,
        getKeycloakToken,
    });

    if (!iframeUrl) {
        return (
            <Flex.Column
                className="proposal-overlay__loading"
                alignItems="center"
                justifyContent="center"
            >
                <Label.Medium.N100>Preview is unavailable.</Label.Medium.N100>
            </Flex.Column>
        );
    }

    return (
        <ShimmerLoadingIframe
            iframeRef={iframeRef}
            src={iframeUrl}
            title={path === "booking" ? "Shared booking" : "Shared proposal"}
            className="proposal-overlay__embed"
            wrapperClassName="proposal-overlay__embed-wrapper"
            readyMessage
        />
    );
}

function useMiniAppTokenRelay({
    getKeycloakToken,
    iframeOrigin,
    iframeRef,
}: {
    getKeycloakToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>;
    iframeOrigin: string | null;
    iframeRef: RefObject<HTMLIFrameElement | null>;
}) {
    const getToken = useEffectEvent(getKeycloakToken);

    useEffect(
        function receiveMiniAppTokenRequests() {
            function receiveMessage(event: MessageEvent<unknown>) {
                if (!iframeOrigin || event.origin !== iframeOrigin) {
                    return;
                }

                if (event.source !== iframeRef.current?.contentWindow) {
                    return;
                }

                const message = parseRequestExamplesMessageData(event.data);
                if (!message || message.type !== TOKEN_REQUEST_TYPE) {
                    return;
                }

                void respondToTokenRequest({
                    forceRefresh: message.forceRefresh,
                    getKeycloakToken: getToken,
                    iframeOrigin,
                    iframeWindow: iframeRef.current?.contentWindow ?? null,
                    requestId: message.requestId,
                });
            }

            window.addEventListener("message", receiveMessage);

            return () => {
                window.removeEventListener("message", receiveMessage);
            };
        },
        [iframeOrigin, iframeRef],
    );
}

async function respondToTokenRequest({
    forceRefresh,
    getKeycloakToken,
    iframeOrigin,
    iframeWindow,
    requestId,
}: {
    forceRefresh?: boolean;
    getKeycloakToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>;
    iframeOrigin: string;
    iframeWindow: Window | null;
    requestId: string;
}): Promise<void> {
    if (!iframeWindow) {
        return;
    }

    const token = await getKeycloakToken(forceRefresh ? { forceRefresh } : undefined);

    if (token) {
        iframeWindow.postMessage(
            {
                type: TOKEN_RESPONSE_TYPE,
                requestId,
                accessToken: token,
            },
            iframeOrigin,
        );
        return;
    }

    iframeWindow.postMessage(
        {
            type: TOKEN_ERROR_TYPE,
            requestId,
            code: forceRefresh ? "expired" : "unavailable",
        },
        iframeOrigin,
    );
}
